import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationService, Notification } from '../services/notificationService';
import styles from '../styles/modules/UpdateModal.module.css';

interface UpdateModalProps {
  onNotificationRead?: () => void;
}

const UpdateModal: React.FC<UpdateModalProps> = ({ onNotificationRead }) => {
  const { t } = useTranslation();
  const [updates, setUpdates] = useState<Notification[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  const fetchUpdates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationService.getMyNotifications();
      
      // Filter for UNREAD updates only
      const updateList = response.notifications.filter(
        (notif: Notification) => notif.category === 'update' && !notif.isRead
      );
      
      setUpdates(updateList);
      if (updateList.length > 0) {
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  const handleNext = () => {
    if (currentIndex < updates.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleMarkAsRead = async () => {
    const currentUpdate = updates[currentIndex];
    if (!currentUpdate || markingRead) return;

    setMarkingRead(true);
    try {
      await notificationService.markAsRead(currentUpdate._id);
      const updated = updates.filter((a) => a._id !== currentUpdate._id);
      setUpdates(updated);

      if (updated.length === 0) {
        setShowModal(false);
        setCurrentIndex(0);
      } else if (currentIndex >= updated.length) {
        setCurrentIndex(updated.length - 1);
      }
      
      // Wait a bit before notifying parent to allow backend to commit
      if (onNotificationRead) {
        setTimeout(() => {
          onNotificationRead();
        }, 300);
      }
    } catch (error) {
      console.error('Error marking update as read:', error);
    } finally {
      setMarkingRead(false);
    }
  };

  if (!showModal || updates.length === 0 || loading) {
    return null;
  }

  const currentUpdate = updates[currentIndex];

  return (
    <div className={styles.updateOverlay} onClick={() => setShowModal(false)}>
      <div className={styles.updateModal} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          className={styles.updateClose}
          onClick={() => setShowModal(false)}
          title={t('common.close')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Content */}
        <div className={styles.updateContent}>
          <div className={styles.updateDate}>
            {new Date(currentUpdate.createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </div>
          <h2 className={styles.updateTitle}>{currentUpdate.title}</h2>
          <div className={styles.updateBody}>{currentUpdate.content}</div>
        </div>

        {/* Navigation */}
        {updates.length > 1 && (
          <>
            <div className={styles.updateCounter}>
              {currentIndex + 1} of {updates.length}
            </div>
            <div className={styles.updateNavigation}>
              <button
                className={styles.btnNav}
                onClick={handlePrev}
                disabled={currentIndex === 0}
                title={t('notifications.previousAnnouncement')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button
                className={styles.btnNav}
                onClick={handleNext}
                disabled={currentIndex === updates.length - 1}
                title={t('notifications.nextAnnouncement')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
          </>
        )}

        <div className={styles.updateActions}>
          <button
            className={styles.btnUpdateAcknowledge}
            onClick={handleMarkAsRead}
            disabled={markingRead}
            title={t('notifications.markAsRead')}
            aria-label={t('notifications.markAsRead')}
          >
            {markingRead ? (
              t('common.loading')
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateModal;
